// Admin page: configure SePay integration (bank info + webhook secret).
// Route: /admin/sepay  (admin only)
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { apiGet, apiPut } from "@/lib/api/client";
import { Eye, EyeOff } from "lucide-react";

interface SepayConfigResponse {
  bank_bin: string;
  account_no: string;
  account_name: string;
  webhook_secret_set: boolean;
  enabled: boolean;
}

interface SepayConfigBody {
  bank_bin: string;
  account_no: string;
  account_name: string;
  webhook_secret: string;
  enabled: boolean;
}

export function SepayConfigPage() {
  const [form, setForm] = useState<SepayConfigBody>({
    bank_bin: "",
    account_no: "",
    account_name: "",
    webhook_secret: "",
    enabled: true,
  });
  const [secretSet, setSecretSet] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiGet<SepayConfigResponse>("/v1/settings/sepay")
      .then((data) => {
        setForm((f) => ({
          ...f,
          bank_bin: data.bank_bin ?? "",
          account_no: data.account_no ?? "",
          account_name: data.account_name ?? "",
          enabled: data.enabled ?? true,
        }));
        setSecretSet(data.webhook_secret_set ?? false);
      })
      .catch(() => setError("Không thể tải cấu hình SePay"))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      await apiPut("/v1/settings/sepay", form);
      setSaved(true);
      if (form.webhook_secret) setSecretSet(true);
      setForm((f) => ({ ...f, webhook_secret: "" })); // clear after save
    } catch (err: unknown) {
      setError((err as { message?: string })?.message ?? "Lỗi lưu cấu hình");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-6 text-muted-foreground">Đang tải...</div>;

  return (
    <div className="max-w-lg space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Cấu hình SePay</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Thông tin tài khoản ngân hàng và webhook secret từ SePay Dashboard.
        </p>
      </div>

      {error && (
        <div className="rounded-md bg-destructive/10 px-4 py-3 text-sm text-destructive">{error}</div>
      )}
      {saved && (
        <div className="rounded-md bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-700">
          Đã lưu cấu hình thành công.
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1">Bank BIN</label>
          <Input
            value={form.bank_bin}
            onChange={(e) => setForm((f) => ({ ...f, bank_bin: e.target.value }))}
            placeholder="970422"
          />
          <p className="text-xs text-muted-foreground mt-1">Mã BIN ngân hàng (VD: MB=970422)</p>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Số tài khoản</label>
          <Input
            value={form.account_no}
            onChange={(e) => setForm((f) => ({ ...f, account_no: e.target.value }))}
            placeholder="0123456789"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Tên chủ tài khoản</label>
          <Input
            value={form.account_name}
            onChange={(e) => setForm((f) => ({ ...f, account_name: e.target.value }))}
            placeholder="NGUYEN VAN A"
          />
          <p className="text-xs text-muted-foreground mt-1">Viết hoa không dấu, tối đa 25 ký tự</p>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            Webhook Secret
            {secretSet && (
              <span className="ml-2 text-xs text-green-600 font-normal">(đã thiết lập)</span>
            )}
          </label>
          <div className="relative">
            <Input
              type={showSecret ? "text" : "password"}
              value={form.webhook_secret}
              onChange={(e) => setForm((f) => ({ ...f, webhook_secret: e.target.value }))}
              placeholder={secretSet ? "Nhập để thay đổi secret..." : "Nhập webhook secret từ SePay"}
              className="pr-10"
            />
            <button
              type="button"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
              onClick={() => setShowSecret((s) => !s)}
            >
              {showSecret ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Lấy từ SePay Dashboard → Webhook → Secret. Để trống = không xác thực (không khuyến khích).
          </p>
        </div>

        <div className="flex items-center gap-3">
          <input
            id="sepay-enabled"
            type="checkbox"
            checked={form.enabled}
            onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
            className="h-4 w-4 rounded border"
          />
          <label htmlFor="sepay-enabled" className="text-sm font-medium cursor-pointer">
            Bật tích hợp SePay
          </label>
        </div>
      </div>

      <Button onClick={handleSave} disabled={saving} className="w-full sm:w-auto">
        {saving ? "Đang lưu..." : "Lưu cấu hình"}
      </Button>
    </div>
  );
}
